import { Box, Button, TextField, Typography } from "@mui/material";
import { Theme } from "@mui/material/styles";
import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { radius } from "src/design/tokens";
import { fontStretch } from "src/design/typography";
import { FilterPills } from "src/grading/components/FilterPills/FilterPills";
import { InlineAlert } from "src/grading/components/InlineAlert/InlineAlert";
import { SideDrawer } from "src/grading/components/SideDrawer/SideDrawer";
import type { ApiError, BenchmarkSex, ModificationKind } from "src/models";
import {
  BenchmarkRow,
  CATEGORY_EVENTS,
  Category,
  EVENT_LABEL,
  EVENT_UNIT,
  EntryMode,
  EntryResponse,
  EventId,
  SessionEntry,
} from "src/models/sessions";
import { usePutEntryMutation } from "src/store/reducers/sessions/sessionsApiSlice";

/** Modes per event, as in `SessionReviewPage`. */
export const EVENT_MODES: Partial<Record<EventId, EntryMode[]>> = {
  "pft-mile": ["run", "walk", "wheel"],
  "pft-pullups": ["pull-up", "chin-up"],
};

const MODIFICATIONS: ModificationKind[] = [
  "none",
  "modification",
  "adaptation",
  "alternative",
];

const MOD_SHORT: Record<ModificationKind, string> = {
  none: "None",
  modification: "Modification",
  adaptation: "Adaptation",
  alternative: "Alternative test",
};

/** Quick note chips (keys under `modification.notes`), as in the prototype. */
const NOTE_CHIPS = [
  "kneePushUp",
  "inclinePushUp",
  "modifiedCurlUp",
  "inclinePlank",
  "flexedArmHang",
  "sixMinuteWalk",
] as const;

export type BenchmarkThresholds = Pick<
  BenchmarkRow,
  "presidentialThreshold" | "nationalThreshold" | "comparison"
>;

/**
 * Benchmark lookup: the session's benchmark rows (filtered here by the student's table and age,
 * clamped to the nearest age row), or a resolver the page supplies.
 */
export type BenchmarkLookup =
  | BenchmarkRow[]
  | ((eventId: EventId) => BenchmarkThresholds | null);

export interface EditScoreStudent {
  id: string;
  firstName: string;
  lastName?: string;
  lastInitial?: string;
  /** Age on test day. */
  age: number | null;
  benchmarkSex: BenchmarkSex | null;
}

export interface EditScoreDrawerProps {
  open: boolean;
  onClose: () => void;
  sessionId: string;
  /** Existing (non-superseded) entry, or null to record a new one. */
  entry: SessionEntry | null;
  student: EditScoreStudent;
  category: Category;
  benchmarks: BenchmarkLookup;
  /** Event to preselect for a new entry (the session or roster choice). */
  defaultEventId?: EventId;
  onSaved?: (response: EntryResponse) => void;
}

export type PreviewTier =
  | "empty"
  | "presidential"
  | "national"
  | "notYet"
  | "participation"
  | "noBenchmark";

const NUMBER_RE = /^\d+(\.\d+)?$/;
const TIME_RE = /^(\d+):([0-5]\d)$/;

/** Parses a score; the mile accepts m:ss or plain seconds. Returns null when invalid. */
export const parseScore = (eventId: EventId, text: string): number | null => {
  const value = text.trim();
  if (eventId === "pft-mile") {
    const match = TIME_RE.exec(value);
    if (match) return Number(match[1]) * 60 + Number(match[2]);
  }
  return NUMBER_RE.test(value) ? Number(value) : null;
};

export const formatScore = (eventId: string, value: number) => {
  if (eventId !== "pft-mile") return String(value);
  const total = Math.round(value);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
};

export const resolveThresholds = (
  benchmarks: BenchmarkLookup,
  eventId: EventId,
  sex: BenchmarkSex | null,
  age: number | null,
): BenchmarkThresholds | null => {
  if (typeof benchmarks === "function") return benchmarks(eventId);
  if (!sex || age === null) return null;
  const rows = benchmarks.filter((r) => r.sex === sex && r.eventId === eventId);
  if (!rows.length) return null;
  return rows.reduce((best, r) =>
    Math.abs(r.age - age) < Math.abs(best.age - age) ? r : best,
  );
};

export const previewTier = (
  value: number | null,
  modification: ModificationKind,
  thresholds: BenchmarkThresholds | null,
): PreviewTier => {
  if (value === null) return "empty";
  if (modification === "adaptation" || modification === "alternative")
    return "participation";
  if (!thresholds) return "noBenchmark";
  const meets = (threshold: number) =>
    thresholds.comparison === "max" ? value <= threshold : value >= threshold;
  if (meets(thresholds.presidentialThreshold)) return "presidential";
  if (meets(thresholds.nationalThreshold)) return "national";
  return "notYet";
};

const previewColors = (theme: Theme, tier: PreviewTier) => {
  const p = theme.palette;
  switch (tier) {
    case "presidential":
      return { bg: p.tierPresidential, fg: p.onTier };
    case "national":
      return { bg: p.tierNational, fg: p.onTier };
    case "participation":
      return { bg: p.tierParticipationBg, fg: p.tierParticipationFg };
    case "notYet":
      return { bg: p.surfaceAlt, fg: p.text.primary };
    case "noBenchmark":
      return { bg: p.surfaceAlt, fg: p.text.secondary };
    default:
      return { bg: p.surfaceAlt, fg: p.tokens.textMuted };
  }
};

const uuid = () =>
  globalThis.crypto?.randomUUID
    ? globalThis.crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const SectionLabel = ({ children }: { children: ReactNode }) => (
  <Typography
    component="span"
    sx={(theme) => ({
      fontSize: 11,
      fontWeight: 800,
      letterSpacing: ".08em",
      textTransform: "uppercase",
      color: theme.palette.tokens.textMuted,
    })}
  >
    {children}
  </Typography>
);

const PillRow = ({ children }: { children: ReactNode }) => (
  <Box
    sx={{
      "& .MuiToggleButtonGroup-root": { flexWrap: "wrap", gap: 0.75 },
    }}
  >
    {children}
  </Box>
);

/**
 * Teacher score edit (prototype `hasEdit`): event, score with a live benchmark preview, mode,
 * modification ladder and note. Saves through the same `putEntry` mutation as `SessionReviewPage`.
 */
export const EditScoreDrawer = ({
  open,
  onClose,
  sessionId,
  entry,
  student,
  category,
  benchmarks,
  defaultEventId,
  onSaved,
}: EditScoreDrawerProps) => {
  const { t } = useTranslation();
  const [putEntry, { isLoading: saving }] = usePutEntryMutation();
  const initialEvent = (entry?.eventId ??
    defaultEventId ??
    CATEGORY_EVENTS[category][0]) as EventId;
  const [eventId, setEventId] = useState<EventId>(initialEvent);
  const [value, setValue] = useState("");
  const [mode, setMode] = useState<EntryMode | undefined>(undefined);
  const [modification, setModification] = useState<ModificationKind>("none");
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  // Reset only when the drawer opens or the entry changes identity, so a refetch that
  // hands the page a new `entry` object does not wipe an edit in progress.
  const entryRef = useRef(entry);
  entryRef.current = entry;
  const entryId = entry?.id;

  useEffect(() => {
    if (!open) return;
    const entry = entryRef.current;
    setEventId(initialEvent);
    setValue(entry ? formatScore(initialEvent, Number(entry.value)) : "");
    setMode(entry?.mode ?? EVENT_MODES[initialEvent]?.[0]);
    setModification(entry?.modification ?? "none");
    setNote(entry?.modificationNote ?? "");
    setError(null);
  }, [open, entryId, initialEvent]);

  const parsed = parseScore(eventId, value);
  const thresholds = useMemo(
    () =>
      resolveThresholds(benchmarks, eventId, student.benchmarkSex, student.age),
    [benchmarks, eventId, student.benchmarkSex, student.age],
  );
  const tier = previewTier(parsed, modification, thresholds);
  const modes = EVENT_MODES[eventId];
  const noteMissing = modification !== "none" && !note.trim();
  const valid = parsed !== null && !noteMissing && (!modes || !!mode);

  const eventOptions = (entry ? [initialEvent] : CATEGORY_EVENTS[category]).map(
    (e) => ({
      value: e,
      label: t(`events.${e}`, { defaultValue: EVENT_LABEL[e] ?? e }),
    }),
  );

  const name = [
    student.firstName,
    student.lastName ??
      (student.lastInitial ? `${student.lastInitial}.` : undefined),
  ]
    .filter(Boolean)
    .join(" ");
  const table = student.benchmarkSex
    ? t("review.benchmarkTableLabel", {
        defaultValue: "{{sex}} table",
        sex: student.benchmarkSex,
      })
    : t("review.benchmarkTableMissing", {
        defaultValue: "Benchmark table not set",
      });
  const subtitle = [
    t(`categories.${category}`),
    student.age !== null
      ? t("review.ageLabel", { defaultValue: "age {{age}}", age: student.age })
      : undefined,
    table,
  ]
    .filter(Boolean)
    .join(" · ");

  const unitLabel =
    eventId === "pft-mile"
      ? t("review.unit.mile", { defaultValue: "m:ss or seconds" })
      : t(`review.unit.${EVENT_UNIT[eventId]}`, {
          defaultValue: EVENT_UNIT[eventId],
        });

  const benchmarkLine = thresholds
    ? t("review.benchmarkLine", {
        defaultValue: "National {{national}} · Presidential {{presidential}}",
        national: formatScore(eventId, thresholds.nationalThreshold),
        presidential: formatScore(eventId, thresholds.presidentialThreshold),
      })
    : t("review.benchmarkUnavailable", {
        defaultValue: "No benchmark row for this student",
      });

  const previewLabel: Record<PreviewTier, string> = {
    empty: t("review.preview.empty", { defaultValue: "Enter a value" }),
    presidential: t("review.preview.presidential", {
      defaultValue: "Meets Presidential",
    }),
    national: t("review.preview.national", { defaultValue: "Meets National" }),
    notYet: t("common.notYet"),
    participation: t("review.preview.participation", {
      defaultValue: "Counts toward Participation",
    }),
    noBenchmark: t("review.noBenchmarkRow", {
      defaultValue: "No benchmark row",
    }),
  };

  const changeEvent = (next: string) => {
    const nextEvent = next as EventId;
    setEventId(nextEvent);
    setValue("");
    setMode(EVENT_MODES[nextEvent]?.[0]);
  };

  const save = async () => {
    if (!valid || parsed === null) return;
    setError(null);
    try {
      const response = await putEntry({
        sessionId,
        entry: {
          id: entry?.id ?? uuid(),
          studentId: student.id,
          eventId,
          attempt: entry?.attempt ?? 1,
          value: parsed,
          unit: EVENT_UNIT[eventId],
          mode: modes ? mode : undefined,
          modification,
          modificationNote: note.trim() || undefined,
          recordedAt: new Date().toISOString(),
        },
      }).unwrap();
      if (response.outcome === "rejected") {
        setError(
          response.error ??
            t("review.entryRejected", {
              defaultValue: "The entry was not accepted.",
            }),
        );
        return;
      }
      onSaved?.(response);
      onClose();
    } catch (e) {
      const message = (e as ApiError | undefined)?.data?.message;
      setError(
        Array.isArray(message)
          ? message.join(", ")
          : (message ??
              t("review.saveEntryError", {
                defaultValue: "Could not save the entry.",
              })),
      );
    }
  };

  return (
    <SideDrawer
      open={open}
      onClose={onClose}
      title={name}
      subtitle={subtitle}
      footer={
        <Box sx={{ display: "flex", gap: 1.25, justifyContent: "flex-end" }}>
          <Button color="inherit" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="contained"
            disabled={!valid || saving}
            onClick={save}
          >
            {t("review.saveScore", { defaultValue: "Save score" })}
          </Button>
        </Box>
      }
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <SectionLabel>{t("console.event")}</SectionLabel>
          <PillRow>
            <FilterPills
              options={eventOptions}
              value={eventId}
              onChange={changeEvent}
              aria-label={t("console.event")}
            />
          </PillRow>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <TextField
            label={`${t("review.score", { defaultValue: "Score" })} · ${unitLabel}`}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            error={value.trim() !== "" && parsed === null}
            helperText={
              value.trim() !== "" && parsed === null
                ? t("review.invalidScore", {
                    defaultValue: "Enter a number, or m:ss for the mile.",
                  })
                : undefined
            }
            inputProps={{ inputMode: "decimal" }}
            autoFocus
            fullWidth
            sx={(theme) => ({
              "& .MuiOutlinedInput-root": {
                borderRadius: "16px",
                minHeight: 72,
                fontSize: 40,
                fontWeight: 800,
                fontStretch: fontStretch.condensed,
                "& .MuiOutlinedInput-notchedOutline": {
                  borderWidth: 2,
                  borderColor: theme.palette.primary.main,
                },
              },
              "& .MuiInputLabel-root": {
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: ".08em",
                textTransform: "uppercase",
                fontStretch: fontStretch.display,
              },
            })}
          />
          <Typography
            sx={(theme) => ({
              fontSize: 12.5,
              fontWeight: 600,
              color: theme.palette.tokens.textMuted,
            })}
          >
            {benchmarkLine}
          </Typography>
          <Box
            component="span"
            role="status"
            aria-live="polite"
            data-testid="score-preview"
            data-tier={tier}
            sx={(theme) => {
              const { bg, fg } = previewColors(theme, tier);
              return {
                alignSelf: "flex-start",
                fontSize: 12,
                fontWeight: 800,
                px: 1.625,
                py: 1.125,
                borderRadius: `${radius.pill}px`,
                bgcolor: bg,
                color: fg,
              };
            }}
          >
            {previewLabel[tier]}
          </Box>
        </Box>

        {modes ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <SectionLabel>{t("console.mode")}</SectionLabel>
            <PillRow>
              <FilterPills
                options={modes.map((m) => ({
                  value: m,
                  label: t(`station.mode.${m}`),
                }))}
                value={mode ?? ""}
                onChange={(next) => setMode(next as EntryMode)}
                aria-label={t("console.mode")}
              />
            </PillRow>
          </Box>
        ) : null}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <SectionLabel>{t("modification.label")}</SectionLabel>
          <PillRow>
            <FilterPills
              options={MODIFICATIONS.map((m) => ({
                value: m,
                label: t(`modification.short.${m}`, {
                  defaultValue: MOD_SHORT[m],
                }),
              }))}
              value={modification}
              onChange={(next) => setModification(next as ModificationKind)}
              aria-label={t("modification.label")}
            />
          </PillRow>
          <Typography
            sx={(theme) => ({
              fontSize: 12,
              lineHeight: 1.4,
              color: theme.palette.tokens.textMuted,
            })}
          >
            {t("modification.ladderExplainer", {
              defaultValue:
                "Modification changes how a rep is cued or counted, and the score still counts. An adaptation or alternative test counts toward Participation only.",
            })}
          </Typography>
        </Box>

        {modification !== "none" ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <TextField
              label={t("console.noteRequired")}
              placeholder={t("review.notePlaceholder", {
                defaultValue: "What changed?",
              })}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              inputProps={{ maxLength: 200 }}
              error={noteMissing}
              helperText={noteMissing ? t("modification.noteRequired") : ""}
              required
              fullWidth
            />
            <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
              {NOTE_CHIPS.map((key) => {
                const label = t(`modification.notes.${key}`);
                return (
                  <Button
                    key={key}
                    variant="outlined"
                    size="small"
                    onClick={() => setNote(label)}
                    sx={{ borderRadius: `${radius.pill}px`, minHeight: 32 }}
                  >
                    {label}
                  </Button>
                );
              })}
            </Box>
          </Box>
        ) : null}

        {error ? <InlineAlert severity="error">{error}</InlineAlert> : null}
      </Box>
    </SideDrawer>
  );
};

export default EditScoreDrawer;
